import "./ReportsRevamp.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLoader } from "../../contexts/LoaderContext";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import ReportService from "../../services/ReportService";
import CustomSelect from "../../components/CoreComponents/CustomSelect";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableComponent, {
  TableColumn,
} from "../../components/TableComponent/TableComponent";
import { SelectChangeEvent } from "@mui/material";
import DateTimePickerComponent from "../../components/DateTimePicker/DateTimePicker";

function ReportsRevamp() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();

  // Report state
  const [movementReports, setMovementReports] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Date range category and sub-option
  const [reportCategory, setReportCategory] = useState("daily");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [subOption, setSubOption] = useState("today");
  const [startDateTime, setStartDateTime] = useState<number | null>(null);
  const [endDateTime, setEndDateTime] = useState<number | null>(null);

  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Refs for CustomSelect components
  const categorySelectRef = useRef<any>(null);
  const subOptionSelectRef = useRef<any>(null);

  // Refs for loader functions
  const showLoaderRef = useRef(showLoader);
  const hideLoaderRef = useRef(hideLoader);
  const loaderRef = useRef(loader);

  useEffect(() => {
    showLoaderRef.current = showLoader;
    hideLoaderRef.current = hideLoader;
    loaderRef.current = loader;
  }, [showLoader, hideLoader, loader]);

  // Helper function to get start of day
  const getStartOfDay = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  // Helper function to get end of day
  const getEndOfDay = (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };

  // Helper function to get date range based on category and option
  const getDateRange = useCallback((category: string, option: string) => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (category) {
      case "daily":
        if (option === "today") {
          start = getStartOfDay(now);
          end = getEndOfDay(now);
        } else {
          // yesterday
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          start = getStartOfDay(yesterday);
          end = getEndOfDay(yesterday);
        }
        break;

      case "weekly":
        if (option === "current") {
          // Current week (Sunday to Saturday)
          const currentDay = now.getDay();
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - currentDay);
          start = getStartOfDay(startOfWeek);
          end = getEndOfDay(now);
        } else {
          // Previous week
          const currentDay = now.getDay();
          const startOfPrevWeek = new Date(now);
          startOfPrevWeek.setDate(now.getDate() - currentDay - 7);
          const endOfPrevWeek = new Date(startOfPrevWeek);
          endOfPrevWeek.setDate(startOfPrevWeek.getDate() + 6);
          start = getStartOfDay(startOfPrevWeek);
          end = getEndOfDay(endOfPrevWeek);
        }
        break;

      case "monthly":
        if (option === "current") {
          // Current month
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          start = getStartOfDay(start);
          end = getEndOfDay(now);
        } else {
          // Previous month
          const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastDayOfPrevMonth = new Date(
            now.getFullYear(),
            now.getMonth(),
            0
          );
          start = getStartOfDay(prevMonth);
          end = getEndOfDay(lastDayOfPrevMonth);
        }
        break;

      case "yearly":
        if (option === "current") {
          // Current year
          start = new Date(now.getFullYear(), 0, 1);
          start = getStartOfDay(start);
          end = getEndOfDay(now);
        } else {
          // Previous year
          const prevYear = now.getFullYear() - 1;
          start = new Date(prevYear, 0, 1);
          start = getStartOfDay(start);
          end = new Date(prevYear, 11, 31);
          end = getEndOfDay(end);
        }
        break;

      case "custom":
        // For custom, keep current values or set to null
        return null;

      default:
        start = getStartOfDay(now);
        end = getEndOfDay(now);
    }

    return {
      start: start.getTime(),
      end: end.getTime(),
    };
  }, []);

  // Set initial date range
  useEffect(() => {
    const range = getDateRange("daily", "today");
    if (range) {
      setStartDateTime(range.start);
      setEndDateTime(range.end);
    }
  }, [getDateRange]);

  // Set initial values for dropdowns
  useEffect(() => {
    if (categorySelectRef.current) {
      categorySelectRef.current.setSeletedOption("daily");
    }
    if (subOptionSelectRef.current) {
      subOptionSelectRef.current.setSeletedOption("today");
    }
  }, []);

  // Fetch reports function
  const fetchReports = useCallback(
    async (suppressLoader?: boolean) => {
      if (isLoading.current) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        isLoading.current = true;

        if (!suppressLoader) {
          showLoaderRef.current();
        } else {
          loaderRef.current.setLoading(true);
        }

        const apiParams = {
          from: startDateTime || undefined,
          to: endDateTime || undefined,
          limit: pageSize,
          search: debouncedSearchQuery || undefined,
          skip: (currentPage - 1) * pageSize,
        };

        const response = await ReportService.getMovementReport(apiParams);

        if (abortControllerRef.current.signal.aborted) {
          return;
        }

        if (response.data && response.data.content) {
          setMovementReports(response.data.content);
          setTotalElements(response.data.page?.totalElements || 0);
          setTotalPages(response.data.page?.totalPages || 0);
        } else {
          setMovementReports([]);
          setTotalElements(0);
          setTotalPages(0);
        }
      } catch (err: any) {
        if (!err.name || err.name !== "AbortError") {
          console.error("Error fetching data:", err);
          showToast("Failed to fetch reports", "error");
          setMovementReports([]);
          setTotalElements(0);
          setTotalPages(0);
        }
      } finally {
        initialLoadDone.current = true;
        isLoading.current = false;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoaderRef.current();
          } else {
            loaderRef.current.setLoading(false);
          }
        }, 200);
      }
    },
    [startDateTime, endDateTime, debouncedSearchQuery, pageSize, currentPage]
  );

  // Initial fetch
  useEffect(() => {
    if (!initialLoadDone.current && startDateTime && endDateTime) {
      fetchReports(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debounce search query to prevent too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch reports when pagination or debounced search changes
  useEffect(() => {
    if (initialLoadDone.current) {
      fetchReports(true); // Use suppressLoader for pagination changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchQuery]);

  // Handle category change
  const handleCategoryChange = useCallback(
    (selectedValue: string | string[]) => {
      const category = selectedValue as string;
      setReportCategory(category);

      if (category === "custom") {
        // For custom, clear dates
        setStartDateTime(null);
        setEndDateTime(null);
        setSubOption("");
        return;
      }

      // Set default sub-option based on category
      let defaultOption = "";
      switch (category) {
        case "daily":
          defaultOption = "today";
          break;
        case "weekly":
        case "monthly":
        case "yearly":
          defaultOption = "current";
          break;
      }

      setSubOption(defaultOption);

      // Update sub-option select
      if (subOptionSelectRef.current) {
        subOptionSelectRef.current.setSeletedOption(defaultOption);
      }

      // Set date range
      const range = getDateRange(category, defaultOption);
      if (range) {
        setStartDateTime(range.start);
        setEndDateTime(range.end);
      }
    },
    [getDateRange]
  );

  // Handle sub-option change
  const handleSubOptionChange = useCallback(
    (selectedValue: string | string[]) => {
      const option = selectedValue as string;
      setSubOption(option);

      const range = getDateRange(reportCategory, option);
      if (range) {
        setStartDateTime(range.start);
        setEndDateTime(range.end);
      }
    },
    [reportCategory, getDateRange]
  );

  // Handle apply filters
  const handleApplyFilters = useCallback(() => {
    fetchReports(true);
    showToast("Filters applied successfully", "success");
  }, [fetchReports]);

  // Handle reset filters
  const handleResetFilters = useCallback(() => {
    setReportCategory("daily");
    setSubOption("today");
    setSearchQuery("");

    if (categorySelectRef.current) {
      categorySelectRef.current.setSeletedOption("daily");
    }
    if (subOptionSelectRef.current) {
      subOptionSelectRef.current.setSeletedOption("today");
    }

    const range = getDateRange("daily", "today");
    if (range) {
      setStartDateTime(range.start);
      setEndDateTime(range.end);
    }

    showToast("Filters reset successfully", "success");
  }, [getDateRange]);

  // Get sub-options based on category
  const getSubOptions = () => {
    switch (reportCategory) {
      case "daily":
        return [
          { value: "today", label: "Today" },
          { value: "yesterday", label: "Yesterday" },
        ];
      case "weekly":
        return [
          { value: "current", label: "Current Week" },
          { value: "previous", label: "Previous Week" },
        ];
      case "monthly":
        return [
          { value: "current", label: "This Month" },
          { value: "previous", label: "Previous Month" },
        ];
      case "yearly":
        return [
          { value: "current", label: "This Year" },
          { value: "previous", label: "Previous Year" },
        ];
      default:
        return [];
    }
  };

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(event.target.value as number);
      setCurrentPage(1); // Reset to first page when changing page size
    },
    []
  );

  // Memoize table data transformation - use real API data matching ReportsMovement
  const tableData = useMemo(() => {
    return movementReports.map((item, index) => ({
      uniqueId: `${item.containerNo}-${item.vehicle?.name || index}-${index}`,
      // Core movement fields from API
      jobNo: item.jobCode,
      jobOrderDate: item.jobOrderDate
        ? new Date(item.jobOrderDate).toLocaleDateString("en-GB")
        : "N/A",
      movementDate: item.startDate
        ? new Date(item.startDate).toLocaleDateString("en-GB")
        : "N/A",
      startDateTime: item.startDate
        ? new Date(item.startDate).toLocaleString("en-GB")
        : "N/A",
      endDateTime: item.endDate
        ? new Date(item.endDate).toLocaleString("en-GB")
        : "N/A",
      activity: item.activity || "N/A",

      // Account and Customer Information
      account: item.account?.name || "N/A",
      accountId: item.account?.id || null,

      // Vessel Information
      vesselName: item.vesselName || "N/A",
      voyageNo: item.voyageNo || "N/A",
      viaNo: item.viaNo || "N/A",
      cuttOffDate: item.cuttOffDate
        ? new Date(item.cuttOffDate).toLocaleDateString("en-GB")
        : "N/A",

      // Transport Information
      transporter: item.transporter?.name || "N/A",
      transporterId: item.transporter?.id || null,
      vehicleNo: item.vehicle?.name || "N/A",
      vehicleId: item.vehicle?.id || null,
      driverName: item.driver?.name || "N/A",
      driverId: item.driver?.id || null,

      // Location Information
      fromLocation: item.fromLocation || "N/A",
      toLocation: item.toLocation || "N/A",

      // Container Information
      containerSize: item.containerSize || "N/A",
      containerNo: item.containerNo || "N/A",
      containerType: item.containerType || "N/A",

      // Legacy fields for backward compatibility
      impExp: item.activity?.includes("IMPORT") ? "IMPORT" : "EXPORT",
      size: item.containerSize || "N/A",
      type: item.containerType || "N/A",
      from: item.fromLocation || "N/A",
      to: item.toLocation || "N/A",
      onAccount: item.account?.name || "N/A",
      truckNo: item.vehicle?.name || "N/A",
      containerNo1: item.containerNo || "N/A",
      containerType1: item.containerType || "N/A",
    }));
  }, [movementReports]);

  // Memoize search suggestions
  const searchSuggestionsMemo = useMemo(() => {
    if (tableData.length > 0) {
      const suggestions = new Set<string>();

      tableData.forEach((row) => {
        Object.values(row).forEach((value) => {
          if (
            typeof value === "string" &&
            value.length > 0 &&
            value !== "N/A"
          ) {
            suggestions.add(value);
          }
        });
      });

      return Array.from(suggestions);
    }
    return [];
  }, [tableData]);

  // Update search suggestions when table data changes
  useEffect(() => {
    setSearchSuggestions(searchSuggestionsMemo);
  }, [searchSuggestionsMemo]);

  // Handle search input changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    // Reset to first page when search changes - this will happen after debounce
    setCurrentPage(1);
  }, []);

  // Table columns configuration - matches ReportsMovement
  const columns: TableColumn[] = useMemo(
    () => [
      { header: "Job Code", accessor: "jobNo", sortable: true },
      { header: "Job Order Date", accessor: "jobOrderDate", sortable: true },
      { header: "Movement Date", accessor: "movementDate", sortable: true },
      { header: "Start Time", accessor: "startDateTime", sortable: true },
      { header: "End Time", accessor: "endDateTime", sortable: true },
      { header: "Activity", accessor: "activity", sortable: true },
      { header: "Account", accessor: "account", sortable: true },
      { header: "Vessel Name", accessor: "vesselName", sortable: true },
      { header: "Voyage No", accessor: "voyageNo", sortable: true },
      { header: "Via No", accessor: "viaNo", sortable: true },
      { header: "Cut-Off Date", accessor: "cuttOffDate", sortable: true },
      { header: "Transporter", accessor: "transporter", sortable: true },
      { header: "Vehicle No", accessor: "vehicleNo", sortable: true },
      { header: "Driver Name", accessor: "driverName", sortable: true },
      { header: "From Location", accessor: "fromLocation", sortable: true },
      { header: "To Location", accessor: "toLocation", sortable: true },
      { header: "Container Size", accessor: "containerSize", sortable: true },
      { header: "Container No", accessor: "containerNo", sortable: true },
      { header: "Container Type", accessor: "containerType", sortable: true },
    ],
    []
  );

  return (
    <div className="reports-page p-[20px]">
      {/* Filters Section */}
      <div className="flex justify-between items-center">
        <div className="flex align-center gap-4">
          {/* Report Category */}
          <div className="min-w-[255px]">
            <label className="block text-left text-grayText required_field_label mb-2">
              Report Period
            </label>
            <CustomSelect
              ref={categorySelectRef}
              options={[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "yearly", label: "Yearly" },
                { value: "custom", label: "Custom" },
              ]}
              label="Report Period"
              placeholder="Select Period"
              onChange={handleCategoryChange}
              className="h-[40px]"
            />
          </div>

          {/* Sub-Option (Only show for non-custom categories) */}
          {reportCategory !== "custom" && (
            <div className="min-w-[255px]">
              <label className="block text-left text-grayText required_field_label mb-2">
                Period Option
              </label>
              <CustomSelect
                ref={subOptionSelectRef}
                options={getSubOptions()}
                label="Period Option"
                placeholder="Select Option"
                onChange={handleSubOptionChange}
                className="h-[40px]"
              />
            </div>
          )}

          {/* Start Date and Time */}
          <div className="min-w-[255px]">
            <label className="block text-left text-grayText required_field_label mb-2">
              Start Date & Time
            </label>
            {reportCategory === "custom" ? (
              <DateTimePickerComponent
                skipMinDate={true}
                value={startDateTime}
                className="h-[40px]"
                onChange={(newValue) => setStartDateTime(newValue)}
              />
            ) : (
              <input
                type="text"
                value={
                  startDateTime
                    ? new Date(startDateTime).toLocaleString("en-GB")
                    : ""
                }
                disabled
                className="h-[40px] w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
              />
            )}
          </div>

          {/* End Date and Time */}
          <div className="min-w-[255px]">
            <label className="block text-left text-grayText required_field_label mb-2">
              End Date & Time
            </label>
            {reportCategory === "custom" ? (
              <DateTimePickerComponent
                skipMinDate={true}
                minDate={startDateTime || undefined}
                value={endDateTime}
                className="h-[40px]"
                onChange={(newValue) => setEndDateTime(newValue)}
              />
            ) : (
              <input
                type="text"
                value={
                  endDateTime
                    ? new Date(endDateTime).toLocaleString("en-GB")
                    : ""
                }
                disabled
                className="h-[40px] w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
              />
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="actions flex align-center gap-2 mt-[21px]">
          <button
            type="button"
            onClick={handleApplyFilters}
            className="primary_button min-w-[122px]"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={handleResetFilters}
            className="secondary_button min-w-[122px]"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex justify-between items-center py-[30px]">
        <div className="flex items-center space-x-2 w-full lg:w-auto">
          <SearchBar
            onSearch={handleSearchChange}
            placeholder="Search reports..."
            showClearButton={true}
            suggestions={searchSuggestions}
            onSuggestionClick={(suggestion) => {
              setSearchQuery(suggestion);
              setCurrentPage(1); // Reset to first page on suggestion click
            }}
          />
        </div>
      </div>

      {/* Reports Table */}
      <TableComponent
        columns={columns}
        data={tableData}
        showCheckboxes={false}
        showSerialNumbers={true}
        searchQuery={searchQuery}
        highlightSearchMatches={true}
        showPagination={true}
        pageSize={pageSize}
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        enableRowHover={true}
        enableClientSideFiltering={false}
        isServerSidePagination={true}
        uniqueAccessor="uniqueId"
        enableHorizontalScroll={true}
      />
    </div>
  );
}

export default ReportsRevamp;
